import { useCreateOrderAndPayMutation } from "../queries/auto-generated/hooks";
import {
  OrderInput,
  Order,
  OrderFulfillmentRecipientInput,
} from "../queries/auto-generated/schemas";
import { useDispatch, useSelector } from "react-redux";
import cart from "../ducks/cart";
import { useHistory } from "react-router-dom";
import { ORDER_PAGE_PATH } from "../router";
import checkout from "../ducks/checkout";
import user from "../ducks/user";
import { RootState } from "../ducks";
import useIdempotencyKey from "./useIdempotencyKey";

const useCreateOrderAndPay = (
  orderRequest: OrderInput | null,
  tipAmount: number | null,
  requestedPickupTime?: string
) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { note: orderNote } = useSelector((state: RootState) => state.cart);
  const { getIdempotencyKey } = useIdempotencyKey();

  const [
    createOrderAndPayMutation,
    { data, loading, error },
  ] = useCreateOrderAndPayMutation();

  const userContact = useSelector((state: RootState) => state.user.contactInfo);

  const createOrderAndPay = async (source_id: string) => {
    try {
      if (orderRequest === null) throw new Error("Invalid order request");

      if (userContact === undefined) throw new Error("Missing contact info");

      const recipient: OrderFulfillmentRecipientInput = {
        display_name: `${userContact.firstName} ${userContact.lastName}`,
        email_address: userContact.email,
        phone_number: userContact.phoneNumber,
        address: {
          first_name: userContact.firstName,
          last_name: userContact.lastName,
        },
      };

      // Form tip money object, if any tip exists
      const tip_money = tipAmount
        ? {
            currency: "USD",
            amount: tipAmount,
          }
        : undefined;

      const { data, errors: gqlErrors } = await createOrderAndPayMutation({
        variables: {
          orderRequest,
          paymentRequest: {
            source_id,
            clientIdempotencyKey: getIdempotencyKey(),
            tip_money,
          },
          recipient,
          requestedPickupTime,
          orderNote,
        },
      });

      if (gqlErrors) throw gqlErrors;

      if (data) {
        const {
          createOrderAndPay: { order, errors },
        } = data;
        if (errors) throw errors;
        if (order === null || order === undefined)
          throw new Error("Order is missing");

        // Move to next
        history.push(ORDER_PAGE_PATH + "/" + order?.id);

        dispatch(user.actions.addOrder(order as Order));
        dispatch(cart.actions.resetCart());
        dispatch(checkout.actions.reset());
      }
    } catch (error) {
      let errorMessages: string[] = [];

      console.error({ error });

      // Handle GraphQL errors
      if (error.graphQLErrors) {
        errorMessages = [
          "Trouble communicating with the server. Please try again or contact customer service.",
        ];
      } else if (Array.isArray(error)) {
        errorMessages = error.map((e) => e.detail);
      } else if (error instanceof Error) {
        errorMessages = [error.message];
      } else {
        errorMessages = [
          "There was an issue. Please contact customer service.",
        ];
      }

      dispatch(checkout.actions.setErrorMessages(errorMessages));
    }
  };

  return {
    createOrderAndPay,
    data,
    loading,
    error,
  };
};

export default useCreateOrderAndPay;
